'use client'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { cn } from '@/ui/utils/style'
import { normalizeForSearch } from '@/utils/strings'
import { useState } from 'react'

export interface MobileTokenFilterProps {
  options: string[]
  selected: string[]
  onSelect: (symbols: string[]) => void
  tokenAddressBySymbol?: Record<string, string>
}

export function MobileTokenFilter({ options, selected, onSelect, tokenAddressBySymbol }: MobileTokenFilterProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredOptions = options.filter((symbol) => {
    const normalizedSymbol = normalizeForSearch(symbol)
    const normalizedQuery = normalizeForSearch(searchQuery)
    return normalizedSymbol.includes(normalizedQuery)
  })

  const handleToggle = (symbol: string) => {
    if (selected.includes(symbol)) {
      onSelect(selected.filter((s) => s !== symbol))
    } else {
      onSelect([...selected, symbol])
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {selected.length > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">{selected.length} selected</span>
          <button type="button" onClick={() => onSelect([])} className="text-primary text-xs hover:underline">
            Clear
          </button>
        </div>
      )}
      <input
        type="text"
        placeholder="Search tokens..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-foreground text-xs placeholder:text-muted-foreground focus:outline-none"
      />
      <div className="max-h-36 overflow-y-auto rounded-md border border-white/10 bg-white/[0.04] p-1">
        <div className="flex flex-col gap-0.5">
          {filteredOptions.map((symbol) => (
            <button
              key={symbol}
              type="button"
              onClick={() => handleToggle(symbol)}
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                selected.includes(symbol)
                  ? 'bg-primary/20 text-primary'
                  : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
              )}
            >
              <TokenIcon
                token={{ symbol: TokenSymbol(symbol), address: tokenAddressBySymbol?.[symbol] }}
                className="h-4 w-4"
              />
              {symbol}
              {selected.includes(symbol) && <span className="ml-auto text-primary text-xs">✓</span>}
            </button>
          ))}
          {filteredOptions.length === 0 && (
            <div className="py-2 text-center text-muted-foreground text-xs">No tokens found</div>
          )}
        </div>
      </div>
    </div>
  )
}
